import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  BookOpen, Plus, Trash2, Edit2, RefreshCw, ShieldCheck,
  Save, X, ChevronDown, ChevronUp, Check, Search,
  AlertTriangle, Ban, CheckCircle, Filter,
  FileDown, Table2, Eye, EyeOff, Globe,
  Target, TrendingUp, Award, Layers
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { apiService } from '@/services/api'
import { toast } from 'sonner'

// ============================================================
// I18N - TRANSLATIONS FR/EN
// ============================================================
type Lang = 'fr' | 'en'

const i18n = {
  fr: {
    legalRef: 'Réf. : Arrêtés n°263/14/MINESEC/IGE et n°419/14/MINESEC/IGE',
    title: 'Suivi des Programmes APC',
    subtitle: 'Approche Par Compétences — 6 séquences, 5 domaines de vie, OTI',
    newProgram: 'Nouveau programme',
    sequence: 'Séquence',
    module: 'Module',
    competency: 'Compétence visée',
    situation: 'Situation de vie',
    lessonsPlanned: 'Leçons planifiées',
    lessonsCompleted: 'Leçons réalisées',
    progress: 'Progression',
    diagnostic: 'Éval. diagnostique',
    formative: 'Éval. formative',
    remediation: 'Remédiation',
    oti: "OTI (Objectif Terminal d'Intégration)",
    details: 'Détails',
    startDate: 'Date début',
    endDate: 'Date fin',
    observations: 'Observations',
    teacher: 'Enseignant',
    class: 'Classe',
    subject: 'Matière',
    loading: 'Chargement...',
    page: 'Page',
    previous: 'Précédent',
    next: 'Suivant',
    searchPlaceholder: 'Rechercher un programme...',
    noData: 'Aucun programme',
    statsTitle: 'Statistiques APC',
    totalPrograms: 'Programmes total',
    completedPrograms: 'Programmes complétés',
    pendingOTI: 'OTI en attente',
    alerts: 'Alertes',
    alertIncomplete: 'Programmes incomplets (<50% sans OTI)',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    cancel: 'Annuler',
    deleteConfirm: 'Supprimer ce programme ?',
    errorLoad: 'Erreur chargement',
    errorSave: 'Erreur sauvegarde',
    errorDelete: 'Erreur suppression',
    entryCreated: 'Programme créé',
    entryUpdated: 'Programme mis à jour',
    entryDeleted: 'Programme supprimé',
    filteredView: 'Vue filtrée',
    fullView: 'Vue complète',
    allTeachers: 'Tous les enseignants',
    allSequences: 'Toutes les séquences',
    allClasses: 'Toutes les classes',
    filter: 'Filtrer',
    reset: 'Réinitialiser',
    conformite: 'Conformité MINESEC APC',
    yes: 'Oui',
    no: 'Non',
    domainFamily: 'Familiale / Sociale',
    domainEconomic: 'Économique / Entreprenariat',
    domainEnvironment: 'Environnement / Santé',
    domainCitizenship: 'Citoyenneté / Démocratie',
    domainMedia: 'Média / Communication',
  },
  en: {
    legalRef: 'Ref: Orders n°263/14/MINESEC/IGE and n°419/14/MINESEC/IGE',
    title: 'CBA Program Tracking',
    subtitle: 'Competency-Based Approach — 6 sequences, 5 life domains, TLO',
    newProgram: 'New program',
    sequence: 'Sequence',
    module: 'Module',
    competency: 'Target competency',
    situation: 'Life situation',
    lessonsPlanned: 'Lessons planned',
    lessonsCompleted: 'Lessons completed',
    progress: 'Progress',
    diagnostic: 'Diag. eval.',
    formative: 'Form. eval.',
    remediation: 'Remediation',
    oti: 'TLO (Terminal Learning Objective)',
    details: 'Details',
    startDate: 'Start date',
    endDate: 'End date',
    observations: 'Observations',
    teacher: 'Teacher',
    class: 'Class',
    subject: 'Subject',
    loading: 'Loading...',
    page: 'Page',
    previous: 'Previous',
    next: 'Next',
    searchPlaceholder: 'Search for a program...',
    noData: 'No program',
    statsTitle: 'CBA Statistics',
    totalPrograms: 'Total programs',
    completedPrograms: 'Completed programs',
    pendingOTI: 'TLO pending',
    alerts: 'Alerts',
    alertIncomplete: 'Incomplete programs (<50% without TLO)',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    deleteConfirm: 'Delete this program?',
    errorLoad: 'Loading error',
    errorSave: 'Save error',
    errorDelete: 'Delete error',
    entryCreated: 'Program created',
    entryUpdated: 'Program updated',
    entryDeleted: 'Program deleted',
    filteredView: 'Filtered view',
    fullView: 'Full view',
    allTeachers: 'All teachers',
    allSequences: 'All sequences',
    allClasses: 'All classes',
    filter: 'Filter',
    reset: 'Reset',
    conformite: 'MINESEC CBA Compliance',
    yes: 'Yes',
    no: 'No',
    domainFamily: 'Family / Social',
    domainEconomic: 'Economic / Entrepreneurship',
    domainEnvironment: 'Environment / Health',
    domainCitizenship: 'Citizenship / Democracy',
    domainMedia: 'Media / Communication',
  }
}

function useTranslation() {
  const [lang, setLang] = useState<Lang>(() => {
    const stored = localStorage.getItem('app_language') as Lang
    return stored === 'en' ? 'en' : 'fr'
  })

  const t = useMemo(() => i18n[lang], [lang])

  const toggleLang = useCallback(() => {
    const newLang: Lang = lang === 'fr' ? 'en' : 'fr'
    setLang(newLang)
    localStorage.setItem('app_language', newLang)
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: newLang }))
  }, [lang])

  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<Lang>
      if (customEvent.detail && (customEvent.detail === 'fr' || customEvent.detail === 'en')) {
        setLang(customEvent.detail)
      }
    }
    window.addEventListener('languageChanged', handler)
    return () => window.removeEventListener('languageChanged', handler)
  }, [])

  // ✅ RETURN TOUJOURS PRÉSENT
  return { t, lang, toggleLang }
}

interface Teacher {
  id: number
  first_name: string
  last_name: string
}

interface ClassItem {
  id: number
  name: string
}

interface Subject {
  id: number
  name?: string
  label_fr?: string
  label_en?: string
}

interface APCRecord {
  id: number
  teacher_id: number
  class_id: number
  subject_id: number
  sequence_number: number
  module_name: string
  competency_target: string
  situation_title: string
  lessons_planned: number
  lessons_completed: number
  evaluation_diagnostic_done: boolean
  evaluation_formative_done: boolean
  remediation_done: boolean
  oti_done: boolean
  start_date: string
  end_date?: string
  observations?: string
  school_id?: number
  academic_year_id?: number
}

const SEQUENCES = [1, 2, 3, 4, 5, 6]

export default function APCTrackingPage() {
  const { t, lang, toggleLang } = useTranslation()
  const { user, role } = useAuthStore()

  const hasFullAccess = ['super_admin', 'admin', 'proviseur', 'censeur'].includes(role || '')
  const canWrite = hasFullAccess || ((user as any)?.permissions || []).includes('apc_tracking_write')

  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [records, setRecords] = useState<APCRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState<APCRecord | null>(null)
  const [formData, setFormData] = useState<Partial<APCRecord>>({
    teacher_id: undefined,
    class_id: undefined,
    subject_id: undefined,
    sequence_number: 1,
    module_name: '',
    competency_target: '',
    situation_title: '',
    lessons_planned: 1,
    lessons_completed: 0,
    evaluation_diagnostic_done: false,
    evaluation_formative_done: false,
    remediation_done: false,
    oti_done: false,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    observations: '',
  })
  const [filterSequence, setFilterSequence] = useState('')
  const [filterTeacher, setFilterTeacher] = useState('')
  const [filterClass, setFilterClass] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const currentUserSchoolId = useMemo(() => {
    const raw = (user as any)?.school_id
    const parsed = typeof raw === 'string' ? parseInt(raw, 10) : raw
    return (typeof parsed === 'number' && !isNaN(parsed)) ? parsed : undefined
  }, [user])

  const loadTeachers = useCallback(async () => {
    try {
      const res: any = await apiService.get('/teachers', {
        params: { school_id: currentUserSchoolId, per_page: 1000, status: 'active' }
      })
      setTeachers(Array.isArray(res) ? res : (res.data?.items || res.data || res.items || []))
    } catch (err) {
      console.error('Error loading teachers:', err)
    }
  }, [currentUserSchoolId])

  const loadClasses = useCallback(async () => {
    try {
      const res: any = await apiService.get('/classes', {
        params: { school_id: currentUserSchoolId, per_page: 1000 }
      })
      setClasses(Array.isArray(res) ? res : (res.data?.items || res.data || res.items || []))
    } catch (err) {
      console.error('Error loading classes:', err)
    }
  }, [currentUserSchoolId])

  const loadSubjects = useCallback(async () => {
    try {
      const res: any = await apiService.get('/subjects', {
        params: { school_id: currentUserSchoolId, per_page: 1000, status: 'active' }
      })
      setSubjects(Array.isArray(res) ? res : (res.data?.items || res.data || res.items || []))
    } catch (err) {
      console.error('Error loading subjects:', err)
    }
  }, [currentUserSchoolId])

  // ─── Matières de l'enseignant (table teacher_subject_classes via /teachers/assignments) ───
  const [teacherSubjects, setTeacherSubjects] = useState<Subject[]>([])

  const loadTeacherSubjects = useCallback(async (teacherId?: number, classId?: number) => {
    if (!teacherId) { setTeacherSubjects([]); return }
    try {
      const res: any = await apiService.get('/teachers/assignments', {
        params: {
          teacher_id: teacherId,
          ...(classId ? { class_id: classId } : {}),
        }
      })
      const raw = Array.isArray(res) ? res : (res.data?.items || res.data || res.items || [])
      const mapped: Subject[] = raw
        .map((a: any) => ({
          id: a.subject_id ?? a.id,
          label_fr: a.subject_name || a.subject_label_fr || a.label_fr || a.name,
          label_en: a.subject_name_en || a.label_en,
          name: a.subject_name || a.name,
        }))
        .filter((s: Subject) => s.id)
      // Déduplication par matière
      const unique = mapped.filter((s, i, arr) => arr.findIndex(x => x.id === s.id) === i)
      setTeacherSubjects(unique)
    } catch (err) {
      console.error('Error loading teacher subjects:', err)
      setTeacherSubjects([])
    }
  }, [])

  const loadRecords = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page, per_page: 20, school_id: currentUserSchoolId }
      if (search) params.search = search
      if (filterSequence) params.sequence_number = parseInt(filterSequence)
      if (filterTeacher) params.teacher_id = parseInt(filterTeacher)
      if (filterClass) params.class_id = parseInt(filterClass)
      const res: any = await apiService.get('/discipline/apc-tracking', { params })
      setRecords(Array.isArray(res) ? res : (res.data?.items || res.data || res.items || []))
    } catch (err) {
      console.error('Error loading APC records:', err)
      toast.error(t.errorLoad)
    } finally {
      setLoading(false)
    }
  }, [page, search, filterSequence, filterTeacher, filterClass, currentUserSchoolId, t])

  useEffect(() => { loadTeachers(); loadClasses(); loadSubjects() }, [loadTeachers, loadClasses, loadSubjects])
  useEffect(() => { loadTeacherSubjects(formData.teacher_id, formData.class_id) }, [formData.teacher_id, formData.class_id, loadTeacherSubjects])
  useEffect(() => { loadRecords() }, [loadRecords])

  const handleSave = async () => {
    if (!formData.teacher_id || !formData.class_id || !formData.subject_id || !formData.module_name) {
      toast.error(lang === 'fr' ? 'Enseignant, classe, matière et module requis' : 'Teacher, class, subject and module required')
      return
    }
    try {
      const payload = { ...formData, school_id: currentUserSchoolId }
      if (editingRecord) {
        await apiService.put(`/discipline/apc-tracking/${editingRecord.id}`, payload)
        toast.success(t.entryUpdated)
      } else {
        await apiService.post('/discipline/apc-tracking', payload)
        toast.success(t.entryCreated)
      }
      setShowModal(false)
      setEditingRecord(null)
      loadRecords()
    } catch (err) {
      toast.error(t.errorSave)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm(t.deleteConfirm)) return
    try {
      await apiService.delete(`/discipline/apc-tracking/${id}`)
      toast.success(t.entryDeleted)
      loadRecords()
    } catch (err) {
      toast.error(t.errorDelete)
    }
  }

  const openEdit = (record: APCRecord) => {
    if (!canWrite) return
    setEditingRecord(record)
    setFormData({ ...record })
    setShowModal(true)
  }

  const openCreate = () => {
    if (!canWrite) return
    setEditingRecord(null)
    setFormData({
      teacher_id: undefined,
      class_id: undefined,
      subject_id: undefined,
      sequence_number: 1,
      module_name: '',
      competency_target: '',
      situation_title: '',
      lessons_planned: 1,
      lessons_completed: 0,
      evaluation_diagnostic_done: false,
      evaluation_formative_done: false,
      remediation_done: false,
      oti_done: false,
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      observations: '',
    })
    setShowModal(true)
  }

  const getProgressPercent = (record: APCRecord) => {
    if (!record.lessons_planned || record.lessons_planned === 0) return 0
    return Math.min(100, Math.round((record.lessons_completed / record.lessons_planned) * 100))
  }

  const stats = useMemo(() => {
    const total = records.length
    const completed = records.filter(r => getProgressPercent(r) >= 100).length
    const pendingOTI = records.filter(r => getProgressPercent(r) >= 50 && !r.oti_done).length
    const incomplete = records.filter(r => getProgressPercent(r) < 50 && !r.oti_done).length
    return { total, completed, pendingOTI, incomplete }
  }, [records])

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <BookOpen className="h-7 w-7 text-primary-600" />
              {t.title}
            </h1>
            <p className="page-subtitle">{t.subtitle}</p>
            <p className="text-xs text-gray-500 mt-1">{t.legalRef}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={toggleLang} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
              <Globe className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{lang === 'fr' ? 'FR' : 'EN'}</span>
            </button>
            {canWrite && (
              <button onClick={openCreate} className="btn-primary whitespace-nowrap">
                <Plus className="mr-2 h-4 w-4" />{t.newProgram}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">{t.totalPrograms}</p>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-primary-600" />
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">{t.completedPrograms}</p>
              <p className="text-xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">{t.pendingOTI}</p>
              <p className="text-xl font-bold text-amber-600">{stats.pendingOTI}</p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">{t.alerts}</p>
              <p className="text-xl font-bold text-red-600">{stats.incomplete}</p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          </div>
          {stats.incomplete > 0 && (
            <p className="text-[10px] text-red-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />{t.alertIncomplete}
            </p>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-outline whitespace-nowrap flex items-center gap-2 ${showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : ''}`}
          >
            <Filter className="w-4 h-4" />
            {lang === 'fr' ? 'Filtres' : 'Filters'}
            {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[160px]">
              <label className="label text-xs text-gray-500 mb-1">{t.sequence}</label>
              <select value={filterSequence} onChange={(e) => setFilterSequence(e.target.value)} className="input w-full">
                <option value="">{t.allSequences}</option>
                {SEQUENCES.map(s => (
                  <option key={s} value={s}>{lang === 'fr' ? `Séquence ${s}` : `Sequence ${s}`}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="label text-xs text-gray-500 mb-1">{t.teacher}</label>
              <select value={filterTeacher} onChange={(e) => setFilterTeacher(e.target.value)} className="input w-full">
                <option value="">{t.allTeachers}</option>
                {teachers.map(tch => (
                  <option key={tch.id} value={tch.id}>{tch.first_name} {tch.last_name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="label text-xs text-gray-500 mb-1">{t.class}</label>
              <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="input w-full">
                <option value="">{t.allClasses}</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => { setFilterSequence(''); setFilterTeacher(''); setFilterClass(''); setSearch(''); }}
              className="btn-outline text-sm whitespace-nowrap"
            >
              <X className="w-3 h-3 mr-1" />{t.reset}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="text-left py-3 px-4 font-medium text-gray-600">{t.sequence}</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">{t.teacher}</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">{t.class}</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">{t.module}</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">{t.progress}</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">{t.oti}</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">{t.details}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    {t.loading}
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    {t.noData}
                  </td>
                </tr>
              ) : (
                records.map((record) => {
                  const teacher = teachers.find(t => t.id === record.teacher_id)
                  const cls = classes.find(c => c.id === record.class_id)
                  const progress = getProgressPercent(record)
                  return (
                    <tr key={record.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                          {lang === 'fr' ? `Séquence ${record.sequence_number}` : `Seq. ${record.sequence_number}`}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900">{teacher?.first_name} {teacher?.last_name}</p>
                      </td>
                      <td className="py-3 px-4 text-gray-600">{cls?.name || '-'}</td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900 max-w-[200px] truncate">{record.module_name}</p>
                        <p className="text-xs text-gray-500 max-w-[200px] truncate">{record.competency_target}</p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${progress >= 100 ? 'bg-green-500' : progress >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{progress}%</span>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {record.lessons_completed}/{record.lessons_planned} {lang === 'fr' ? 'leçons' : 'lessons'}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        {record.oti_done ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                            <CheckCircle className="w-3 h-3" /> {t.yes}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-medium">
                            <Ban className="w-3 h-3" /> {t.no}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(record)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                            title={lang === 'fr' ? 'Modifier' : 'Edit'}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {canWrite && (
                            <button
                              onClick={() => handleDelete(record.id)}
                              className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition-colors"
                              title={lang === 'fr' ? 'Supprimer' : 'Delete'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && canWrite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingRecord ? (lang === 'fr' ? 'Modifier le programme' : 'Edit program') : t.newProgram}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">{t.teacher} *</label>
                  <select
                    value={formData.teacher_id || ''}
                    onChange={(e) => setFormData({ ...formData, teacher_id: parseInt(e.target.value), subject_id: undefined })}
                    className="input w-full"
                  >
                    <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    {teachers.map((tch) => (
                      <option key={tch.id} value={tch.id}>{tch.first_name} {tch.last_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t.class} *</label>
                  <select
                    value={formData.class_id || ''}
                    onChange={(e) => setFormData({ ...formData, class_id: parseInt(e.target.value), subject_id: undefined })}
                    className="input w-full"
                  >
                    <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t.subject} *</label>
                  <select
                    value={formData.subject_id || ''}
                    onChange={(e) => setFormData({ ...formData, subject_id: parseInt(e.target.value) })}
                    className="input w-full"
                  >
                    <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    {(teacherSubjects.length > 0 ? teacherSubjects : subjects).map((s) => (
                      <option key={s.id} value={s.id}>{s.label_fr || s.name}</option>
                    ))}
                  </select>
                  {formData.teacher_id && teacherSubjects.length === 0 && (
                    <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {lang === 'fr' ? 'Aucune matière assignée à cet enseignant — toutes les matières affichées' : 'No subject assigned to this teacher — showing all subjects'}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t.sequence} *</label>
                  <select
                    value={formData.sequence_number || 1}
                    onChange={(e) => setFormData({ ...formData, sequence_number: parseInt(e.target.value) })}
                    className="input w-full"
                  >
                    {SEQUENCES.map(s => (
                      <option key={s} value={s}>{lang === 'fr' ? `Séquence ${s}` : `Sequence ${s}`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t.module} *</label>
                  <input
                    type="text"
                    value={formData.module_name || ''}
                    onChange={(e) => setFormData({ ...formData, module_name: e.target.value })}
                    className="input w-full"
                    placeholder={lang === 'fr' ? 'Nom du module APC...' : 'CBA module name...'}
                  />
                </div>
              </div>

              <div>
                <label className="label">{t.competency}</label>
                <input
                  type="text"
                  value={formData.competency_target || ''}
                  onChange={(e) => setFormData({ ...formData, competency_target: e.target.value })}
                  className="input w-full"
                  placeholder={lang === 'fr' ? 'Compétence visée...' : 'Target competency...'}
                />
              </div>

              <div>
                <label className="label">{t.situation}</label>
                <input
                  type="text"
                  value={formData.situation_title || ''}
                  onChange={(e) => setFormData({ ...formData, situation_title: e.target.value })}
                  className="input w-full"
                  placeholder={lang === 'fr' ? "Situation de vie / d'apprentissage..." : 'Life / learning situation...'}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t.lessonsPlanned}</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.lessons_planned || 1}
                    onChange={(e) => setFormData({ ...formData, lessons_planned: parseInt(e.target.value) })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="label">{t.lessonsCompleted}</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.lessons_completed || 0}
                    onChange={(e) => setFormData({ ...formData, lessons_completed: parseInt(e.target.value) })}
                    className="input w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t.startDate}</label>
                  <input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="label">{t.endDate}</label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>

              {/* Checkboxes évaluations */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">{lang === 'fr' ? "Étapes d'évaluation" : 'Evaluation steps'}</p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.evaluation_diagnostic_done || false}
                      onChange={(e) => setFormData({ ...formData, evaluation_diagnostic_done: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm text-gray-700">{t.diagnostic}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.evaluation_formative_done || false}
                      onChange={(e) => setFormData({ ...formData, evaluation_formative_done: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm text-gray-700">{t.formative}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.remediation_done || false}
                      onChange={(e) => setFormData({ ...formData, remediation_done: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm text-gray-700">{t.remediation}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.oti_done || false}
                      onChange={(e) => setFormData({ ...formData, oti_done: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm text-gray-700">{t.oti}</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="label">{t.observations}</label>
                <textarea
                  value={formData.observations || ''}
                  onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                  className="input w-full min-h-[80px]"
                  placeholder={lang === 'fr' ? 'Observations complémentaires...' : 'Additional observations...'}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowModal(false)} className="btn-outline">{t.cancel}</button>
                <button onClick={handleSave} className="btn-primary">{editingRecord ? t.save : (lang === 'fr' ? 'Créer' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}